import { useMutation, useQueryClient } from '@tanstack/react-query';
import { projectTemplateApi } from '@src/apis/admin';
import type {
  UpdateProjectTemplateRequestModel,
  ChangeOrderRequestModel,
  ProjectTemplateInfoResponseModel,
} from '@uniquegood/realworld-admin-interface';

/**
 * 템플릿 수정 mutation
 */
export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['updateTemplate'],
    mutationFn: async ({
      templateId,
      body,
    }: {
      templateId: string;
      body: UpdateProjectTemplateRequestModel;
    }) => {
      const { data } = await projectTemplateApi.updateTemplate(templateId, undefined, body);
      return data;
    },
    onSuccess: (response) => {
      // API 응답에서 업데이트된 템플릿 객체를 받아서 캐시에 직접 업데이트
      if (response?.data) {
        queryClient.setQueryData<ProjectTemplateInfoResponseModel[]>(
          ['templates'],
          (oldData) => {
            if (!oldData) return oldData;
            return oldData.map((template) =>
              template.id === response.data.id ? response.data : template
            );
          }
        );
      }
    },
  });
};

/**
 * 템플릿 순서 변경 mutation
 */
export const useReorderTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['reorderTemplate'],
    mutationFn: async ({
      templateId,
      newOrder,
      reorderedItems,
    }: {
      templateId: string;
      newOrder: number;
      reorderedItems: ProjectTemplateInfoResponseModel[];
    }) => {
      const body: ChangeOrderRequestModel = { newOrder };
      const { data } = await projectTemplateApi.reorderTemplate(templateId, undefined, body);
      return { data, templateId, newOrder, reorderedItems };
    },
    onMutate: async ({ reorderedItems }) => {
      // 낙관적 업데이트: 드래그 완료 즉시 UI 업데이트
      await queryClient.cancelQueries({ queryKey: ['templates'] });
      
      const previousData = queryClient.getQueryData<ProjectTemplateInfoResponseModel[]>(['templates']);
      
      // 새로운 순서로 모든 템플릿의 order 업데이트
      const updatedData = reorderedItems.map((item, index) => ({
        ...item,
        order: index + 1,
      }));
      
      queryClient.setQueryData<ProjectTemplateInfoResponseModel[]>(['templates'], updatedData);
      
      return { previousData };
    },
    onError: (_error, _variables, context) => {
      // 에러 발생 시 이전 상태로 롤백
      if (context?.previousData) {
        queryClient.setQueryData<ProjectTemplateInfoResponseModel[]>(['templates'], context.previousData);
      }
    },
    onSuccess: () => {
      // 성공 시 쿼리 무효화하여 서버 데이터와 동기화
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
};

